import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiBookOpen, FiPrinter, FiAward, FiSlash, FiDownload,
} from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  diariosOficiaisService, CADERNO_LABEL,
  type EdicaoDetalhe, type Caderno,
} from '../../services/diariosOficiais.service';
import { formatarDataBR, formatarDataExtenso } from '../../lib/data';

const TIPO_ATO_LABEL: Record<string, string> = {
  portaria: 'Portaria',
  decreto: 'Decreto',
  lei: 'Lei',
  lei_complementar: 'Lei Complementar',
  resolucao: 'Resolução',
  instrucao_normativa: 'Instrução Normativa',
  edital: 'Edital',
  outros: 'Ato',
};

export function DiarioOficialDetalhePublicoPage() {
  const { id } = useParams<{ id: string }>();
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [edicao, setEdicao] = useState<EdicaoDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!id) return;
    setCarregando(true);
    setErro(null);
    const estabId = getEstabelecimentoAtualId();
    Promise.all([
      diariosOficiaisService.buscar(id),
      supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', estabId).maybeSingle(),
    ])
      .then(([dados, e]) => {
        if ('erro' in dados) {
          setErro(mensagemErro(dados.erro));
        } else {
          setEdicao(dados);
        }
        if (e.data) setEstab(e.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [id]);

  if (carregando) {
    return (
      <PortalPage titulo="Diário Oficial" icone={<FiBookOpen size={26} />} largura="media" voltarHref="/transparencia/diario-oficial" voltarLabel="Voltar à lista">
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }
  if (erro || !edicao) {
    return (
      <PortalPage
        titulo="Diário Oficial"
        icone={<FiBookOpen size={26} />}
        largura="media"
        voltarHref="/transparencia/diario-oficial"
        voltarLabel="Voltar à lista"
        breadcrumbs={[{ label: 'Transparência' }, { label: 'Diário Oficial', href: '/transparencia/diario-oficial' }]}
      >
        <Alert variante="erro">{erro ?? 'Edição não encontrada.'}</Alert>
      </PortalPage>
    );
  }

  const nomeEstab = edicao.estabelecimento_nome ?? estab?.nome;

  return (
    <PortalPage
      titulo={edicao.numero}
      subtitulo={[nomeEstab, edicao.data_publicacao && formatarDataExtenso(edicao.data_publicacao)].filter(Boolean).join(' · ')}
      icone={<FiBookOpen size={26} />}
      largura="media"
      voltarHref="/transparencia/diario-oficial"
      voltarLabel="Voltar à lista"
      breadcrumbs={[
        { label: 'Transparência' },
        { label: 'Diário Oficial', href: '/transparencia/diario-oficial' },
        { label: edicao.numero },
      ]}
      acoes={
        edicao.pdf_url ? (
          <a
            href={edicao.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
          >
            <FiDownload size={15} /> Baixar edição em PDF
          </a>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <div className="flex items-center gap-2 print:hidden flex-wrap">
          <Button variant="outline" onClick={() => window.print()}>
            <FiPrinter size={14} /> Imprimir versão web
          </Button>
        </div>

        {/* Capa */}
        <SectionCard>
          <div className="text-center py-6 border-b border-slate-200 mb-4">
            <div className="text-xs uppercase tracking-widest text-slate-600">Diário Oficial Municipal</div>
            <h1 className="text-2xl font-bold text-slate-900 mt-2">{edicao.titulo}</h1>
            <div className="text-sm text-slate-600 mt-2">
              {edicao.numero}
              {edicao.data_publicacao && (
                <> · <span className="capitalize">{formatarDataExtenso(edicao.data_publicacao)}</span></>
              )}
            </div>
            {edicao.caderno && edicao.caderno !== 'normal' && (
              <div className="mt-2 inline-block px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                {CADERNO_LABEL[edicao.caderno as Caderno]}
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500 text-center">
            Esta edição contém {edicao.atos.length} {edicao.atos.length === 1 ? 'ato oficial' : 'atos oficiais'}.
          </div>
        </SectionCard>

        {/* Sumário */}
        {edicao.atos.length > 0 && (
          <SectionCard titulo="Sumário" icone={<FiBookOpen size={18} />} className="print:hidden">
            <ol className="space-y-1 text-sm">
              {edicao.atos.map((a, i) => {
                const tipoLabel = a.tipo_ato ? TIPO_ATO_LABEL[a.tipo_ato] ?? a.tipo_ato : 'Ato';
                return (
                  <li key={a.id}>
                    <a
                      href={`#ato-${i + 1}`}
                      className="flex items-baseline gap-2 hover:text-institutional"
                    >
                      <span className="text-slate-400 tabular-nums w-6">{i + 1}.</span>
                      <span className="font-medium">
                        {tipoLabel}{a.numero_publicado && <> nº {a.numero_publicado}</>}
                      </span>
                      <span className="text-slate-600 truncate flex-1 min-w-0">— {a.ementa || a.titulo}</span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </SectionCard>
        )}

        {/* Atos completos */}
        {edicao.atos.map((ato, i) => (
          <SectionCard key={ato.id}>
            <div id={`ato-${i + 1}`} className="text-center py-4 border-b border-slate-200 mb-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Ato {i + 1} de {edicao.atos.length}
              </div>
              <div className="text-sm font-semibold text-slate-900 mt-1">
                {ato.tipo_ato ? TIPO_ATO_LABEL[ato.tipo_ato] ?? ato.tipo_ato : 'Ato'}
                {ato.numero_publicado && <> nº {ato.numero_publicado}</>}
              </div>
              {ato.revogado ? (
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full font-semibold">
                  <FiSlash size={11} /> Revogado posteriormente
                </div>
              ) : (
                <div className="mt-1">
                  <FiAward className="inline text-slate-400" size={14} />
                </div>
              )}
            </div>

            {ato.ementa && (
              <div className="mb-4 pl-6 border-l-2 border-slate-300">
                <p className="text-sm text-slate-700 italic leading-relaxed">{ato.ementa}</p>
              </div>
            )}

            {ato.corpo && (
              <div
                className="prose prose-slate prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: ato.corpo }}
              />
            )}

            {(ato.autoridade_nome || ato.autoridade_cargo) && (
              <div className="mt-8 text-center">
                <div className="inline-block">
                  <div className="border-t border-slate-400 w-64 mb-1"></div>
                  <div className="text-sm font-semibold text-slate-900">{ato.autoridade_nome ?? '—'}</div>
                  <div className="text-xs text-slate-600">{ato.autoridade_cargo ?? '—'}</div>
                </div>
              </div>
            )}

            {ato.data_publicacao && (
              <div className="mt-4 text-center text-xs text-slate-500">
                Publicado originalmente em {formatarDataBR(ato.data_publicacao)}
              </div>
            )}
          </SectionCard>
        ))}

        <div className="text-xs text-slate-500 text-center print:hidden">
          {edicao.publicada_em && <>Edição publicada em {formatarDataBR(edicao.publicada_em)}. </>}
          Endereço permanente: <span className="font-mono">{window.location.href}</span>
        </div>
      </div>
    </PortalPage>
  );
}

const ERROS: Record<string, string> = {
  nao_encontrado: 'Edição não encontrada ou removida.',
  modulo_invalido: 'Documento inválido.',
  nao_publicada: 'Esta edição ainda não foi publicada.',
};

function mensagemErro(c: string): string {
  return ERROS[c] ?? 'Não foi possível carregar a edição.';
}
